import { useRef, useState } from "react"
import DatePicker, { registerLocale } from "react-datepicker"
import { ro } from "date-fns/locale"
import "react-datepicker/dist/react-datepicker.css"
import "./styles.css"

registerLocale("ro", ro)

interface DateRangePickerProps {
  startDate: Date | null
  endDate: Date | null
  onChange: (start: Date | null, end: Date | null) => void
  minDate?: Date | null
  maxDate?: Date | null
  label?: string | null
}

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#486af6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)

export const DateRangePicker = ({ startDate, endDate, onChange, minDate, maxDate, label = "Perioada" }: DateRangePickerProps) => {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const formatDate = (date: Date | null) =>
    date ? date.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : null

  const handleChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    onChange(start, end)
    if (start && end) setOpen(false)
  }

  return (
    <div ref={wrapperRef} className="relative">
      {label && <p className="jobbin-input text-figmaPrimary mb-4">{label}</p>}

      <div
        className="flex flex-col sm:flex-row gap-3 cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <div className={`flex-1 bg-white rounded-[12px] px-5 py-[14px] flex items-center gap-3 transition-shadow ${open ? "ring-2 ring-figmaPrimary" : ""}`}>
          <CalendarIcon />
          <div>
            <p className="text-[11px] text-figmaDarkGray leading-none mb-1">Data început</p>
            <p className={`text-[14px] font-medium ${startDate ? "text-figmaBlack" : "text-figmaDarkGray"}`}>
              {formatDate(startDate) ?? "Selectează..."}
            </p>
          </div>
        </div>

        <div className="hidden sm:flex items-center text-figmaDarkGray">→</div>

        <div className={`flex-1 bg-white rounded-[12px] px-5 py-[14px] flex items-center gap-3 transition-shadow ${open ? "ring-2 ring-figmaPrimary" : ""}`}>
          <CalendarIcon />
          <div>
            <p className="text-[11px] text-figmaDarkGray leading-none mb-1">Data sfârșit</p>
            <p className={`text-[14px] font-medium ${endDate ? "text-figmaBlack" : "text-figmaDarkGray"}`}>
              {formatDate(endDate) ?? "Selectează..."}
            </p>
          </div>
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="jobbin-datepicker-wrapper w-full max-h-[90vh] overflow-auto rounded-t-2xl bg-white p-2 shadow-xl sm:w-auto sm:rounded-2xl sm:p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 pb-1 pt-2">
              <p className="text-sm font-semibold text-figmaBlack">Alege perioada</p>
              <button
                type="button"
                className="text-sm font-semibold text-figmaPrimary"
                onClick={() => setOpen(false)}
              >
                Gata
              </button>
            </div>
            <DatePicker
              selected={startDate}
              startDate={startDate}
              endDate={endDate}
              onChange={(dates) => handleChange(dates as [Date | null, Date | null])}
              selectsRange
              inline
              minDate={minDate !== undefined ? (minDate ?? undefined) : (() => { const t = new Date(); t.setHours(0,0,0,0); return t })()}
              maxDate={maxDate ?? undefined}
              openToDate={startDate ?? new Date()}
              monthsShown={1}
              showDisabledMonthNavigation
              calendarClassName="jobbin-calendar"
              locale="ro"
            />
          </div>
        </div>
      )}
    </div>
  )
}
